import React from 'react';
import { useSelector } from 'react-redux';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { IconButton } from '../../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Values } from '../../useRetirementIncome';
import { Dot } from '../../../../../../../../CommonComponents/OPL/Dot/Dot';
import { Button } from '../../../../../../../../CommonComponents/OPL/Button/Button';
import { formatCurrency } from '../../../../../../../../CommonUtils/formatNumber';
import { useBreakdown } from './useBreakdown';
import { selectIsCanadian } from '../../../../../../../../CommonSelectors/CommonAreaSelector';

const Separator = () => {
  return <Box height={1} bg="neutral.200" mb="8" />;
};

interface Props {
  values: Values;
  categories: string[];
  selectedIndex: number | null;
  setSelectedIndex(selectedIndex: number | null): void;
}

export const Breakdown = ({ values, categories, selectedIndex, setSelectedIndex }: Props) => {
  const { breakDown, showDetails, setShowDetails, retirementIncomeColors } = useBreakdown();

  const isCanadian = useSelector(selectIsCanadian);

  if (selectedIndex === null) {
    return null;
  }

  return (
    <Box
      width={360}
      px="24"
      py="16"
      borderColor="neutral.200"
      borderLeftWidth={1}
      borderLeftStyle="solid"
    >
      <Box display="flex" alignItems="center">
        <IconButton
          icon="fas fa-chevron-left"
          variant="subtleInactive"
          disabled={selectedIndex === 0}
          onClick={() => setSelectedIndex(selectedIndex - 1)}
          aria-label="Select previous year"
        />

        <Box display="flex" flex={1} justifyContent="center">
          <Text fontSize="h6" fontWeight={600} mr="8">
            Monthly Income Detail
          </Text>

          <Text as="span" fontSize="h6" fontWeight={600} color="primary.500">
            {categories[selectedIndex]}
          </Text>
        </Box>

        <IconButton
          aria-label="Select next year"
          icon="fas fa-chevron-right"
          variant="subtleInactive"
          disabled={selectedIndex === values.incomeShortage.length - 1}
          onClick={() => setSelectedIndex(selectedIndex + 1)}
        />
      </Box>

      <Box my="12" display="flex" justifyContent="flex-end">
        <Button variant="subtleActive" size="small" onClick={() => setShowDetails(!showDetails)}>
          {showDetails ? 'Hide Details' : 'Show Details'}
        </Button>
      </Box>

      <Box>
        {breakDown.map(({ key, name, color, hasDot, fontWeight, isDetail }) => {
          if (name === 'separator') {
            return <Separator key={key} />;
          }

          if (isDetail && !showDetails) {
            return null;
          }

          if (isCanadian && key === 'socialSecurity') {
            return null;
          }

          return (
            <Box key={key} display="flex" mb="8">
              <Box display="flex" alignItems="center" flex={1}>
                {hasDot && (
                  <Dot
                    bg={retirementIncomeColors[key].bg}
                    borderColor={retirementIncomeColors[key].border}
                    mr="8"
                  />
                )}

                <Text fontWeight={fontWeight || 500} color={color || 'neutral.700'}>
                  {name}
                </Text>
              </Box>

              <Text variant="tabular" fontWeight={fontWeight || 500} color={color || 'neutral.700'}>
                {formatCurrency(values[key][selectedIndex])}
              </Text>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
